import { useQuery } from "@tanstack/react-query";

import {
  TGetInvoiceSettingResDto,
  TGetInvoiceTemplateListResDto,
  TUpdateInvoiceSettingReqDto,
  TUpdateInvoiceSettingResDto,
} from "@/api/services/main/organization/invoice-setting/dtos/invoice-setting.dto";
import { CACHED_TIME } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";

/**
 * 請求設定 (organization invoice setting) API — account-backend main API:
 *   GET /organization/invoice-setting            -> current setting (or system defaults)
 *   PUT /organization/invoice-setting            -> upsert -> { id }
 *   GET /organization/invoice-setting/templates  -> active invoice templates (ordered)
 */
const BASE = "/organization/invoice-setting";

export const fetchInvoiceSettingApi = async (): Promise<TGetInvoiceSettingResDto> => {
  const { data } = await clientApi.get<TGetInvoiceSettingResDto>(BASE);
  return data;
};

export const updateInvoiceSettingApi = async (payload: TUpdateInvoiceSettingReqDto): Promise<TUpdateInvoiceSettingResDto> => {
  const { data } = await clientApi.put<TUpdateInvoiceSettingResDto>(BASE, payload);
  return data;
};

export const fetchInvoiceTemplatesApi = async (): Promise<TGetInvoiceTemplateListResDto> => {
  const { data } = await clientApi.get<TGetInvoiceTemplateListResDto>(`${BASE}/templates`);
  return data;
};

/** 請求設定 fetch — GET returns system defaults when no row exists, so `data` is never blank. */
export const useInvoiceSetting = () =>
  useQuery({
    queryKey: [QUERY_KEYS.INVOICE_SETTING.FETCH_DETAIL],
    queryFn: fetchInvoiceSettingApi,
  });

/** 請求書テンプレート一覧 — cached; replaces the FE hardcoded template list. */
export const useInvoiceTemplates = () => {
  const query = useQuery({
    queryKey: [QUERY_KEYS.INVOICE_SETTING.FETCH_TEMPLATES],
    queryFn: fetchInvoiceTemplatesApi,
    staleTime: CACHED_TIME,
    gcTime: CACHED_TIME,
  });

  return { ...query, templates: query.data?.data ?? [] };
};
